import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MyTimeSelect } from "my-time/components";
import { useOcProperty } from "oc/hooks";

type MyTimeSelectMyTimeTypeProps = {
  data: {
    showMyTimeTypeSelect?: boolean;
    originalStartTime?: Date | string;
    includeStandByCall?: boolean;
    myTimeType?: string;
    // add other properties of data as needed
  };
  selectedMyTimeType: string | number;
  onSelectMyTimeType: (value: string | number) => void;
};

export const MyTimeSelectMyTimeType = ({
  data,
  selectedMyTimeType,
  onSelectMyTimeType,
}: MyTimeSelectMyTimeTypeProps) => {
  const { t } = useTranslation();
  const [showMyTimeSelect, setShowMyTimeSelect] = useState(false);

  // Check if PERSONAL_LEAVE is enabled
  const { data: personalLeaveEnabled, loading: personalLeaveLoading } = useOcProperty({
    resource: "myTime",
    key: "personalLeaveEnabled",
  });

  // STAND_BY_CALL only appears when there's standby (originalStartTime or includeStandByCall flag)
  const includeStandByCall = data?.originalStartTime || data?.includeStandByCall;
  // Only enable PERSONAL_LEAVE if the property is explicitly "true" AND not loading
  const isPersonalLeaveEnabled = !personalLeaveLoading && (personalLeaveEnabled === "true" || personalLeaveEnabled === true);

  const selectableMyTimeTypes = useMemo(() => {
    const types = [];

    // If editing WORKING_TIME, include it as the first option
    if (data?.myTimeType === "WORKING_TIME") {
      types.push({ label: t("MY_TIME_WORKING_TIME"), value: "WORKING_TIME" });
    }

    types.push({ label: t("MY_TIME_OVERTIME"), value: "OVERTIME" });

    if (isPersonalLeaveEnabled) {
      types.push({ label: t("MY_TIME_PERSONAL_LEAVE"), value: "PERSONAL_LEAVE" });
    }

    if (includeStandByCall) {
      types.push({ label: t("MY_TIME_STAND_BY_CALL"), value: "STAND_BY_CALL" });
    }

    return types;
  }, [t, isPersonalLeaveEnabled, includeStandByCall, data?.myTimeType]);

  // Auto-select if only one option available, but NOT when editing WORKING_TIME
  useEffect(() => {
    if (data?.myTimeType === "WORKING_TIME") {
      // When editing WORKING_TIME, don't auto-select - keep the original type
      return;
    }

    if (selectableMyTimeTypes.length === 1 && !selectedMyTimeType) {
      onSelectMyTimeType(selectableMyTimeTypes[0].value);
    }
  }, [selectableMyTimeTypes, selectedMyTimeType, onSelectMyTimeType, data?.myTimeType]);

  // Show type selector if requested and multiple options available
  // Don't show for WORKING_TIME edit (only for add overtime/leave)
  if (data?.showMyTimeTypeSelect && !showMyTimeSelect && data?.myTimeType !== "WORKING_TIME") {
    if (selectableMyTimeTypes.length > 1) {
      setShowMyTimeSelect(true);
    }
  }

  return (
    <MyTimeSelect
      id="id-my-time-type-select"
      label={t("MY_TIME_TYPE")}
      show={showMyTimeSelect && selectableMyTimeTypes.length > 1}
      value={selectedMyTimeType || ""}
      setValue={onSelectMyTimeType}
      values={selectableMyTimeTypes}
    />
  );
};

export default MyTimeSelectMyTimeType;
